import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { inputClass } from "./FormField";

export type CatalogueItem = {
  id: number;
  name: string;
  /** Ligne secondaire : agents, ville, assureurs… */
  detail?: string;
  /** Avertissement affiché si l'élément est coché (ex. transfert depuis un autre assureur). */
  warning?: string;
};

type Props = {
  title: string;
  items: CatalogueItem[];
  selected: number[];
  onChange: (ids: number[]) => void;
  empty: string;
};

/** Liste à cocher du catalogue commun (entreprises ou prestataires), avec recherche. */
export function CataloguePicker({ title, items, selected, onChange, empty }: Props) {
  const [query, setQuery] = useState("");
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((item) => `${item.name} ${item.detail ?? ""}`.toLowerCase().includes(q)) : items;
  }, [items, query]);

  const toggle = (id: number, checked: boolean) =>
    onChange(checked ? [...selected, id] : selected.filter((value) => value !== id));

  // Coche ou décoche d'un coup les éléments affichés (après recherche éventuelle).
  const allVisible = visible.length > 0 && visible.every((item) => selected.includes(item.id));
  const toggleVisible = () => {
    const ids = visible.map((item) => item.id);
    onChange(allVisible ? selected.filter((id) => !ids.includes(id)) : [...new Set([...selected, ...ids])]);
  };

  return (
    <div className="rounded-lg border border-border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <span className="text-sm font-semibold text-foreground">
          {title} <span className="font-normal text-muted">({selected.length} / {items.length})</span>
        </span>
        {items.length > 6 ? (
          <label className="relative w-full sm:w-56">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher"
              aria-label={`Rechercher dans ${title}`}
              className={`${inputClass} py-1.5 pl-8`}
            />
          </label>
        ) : null}
        {items.length > 6 ? (
          <button type="button" onClick={toggleVisible} className="text-xs font-medium text-primary hover:underline">
            {allVisible ? "Tout décocher" : "Tout cocher"}
          </button>
        ) : null}
      </div>
      <ul className="max-h-64 overflow-y-auto px-3 py-2">
        {visible.length === 0 ? <li className="py-1 text-sm text-muted">{items.length === 0 ? empty : "Aucun résultat."}</li> : null}
        {visible.map((item) => {
          const checked = selected.includes(item.id);
          return (
            <li key={item.id}>
              <label className="flex cursor-pointer items-start gap-2 rounded-md px-1 py-1.5 text-sm hover:bg-accent/20">
                <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => toggle(item.id, e.target.checked)} />
                <span className="min-w-0">
                  <span className="block font-medium text-foreground">{item.name}</span>
                  {item.detail ? <span className="block text-xs text-muted">{item.detail}</span> : null}
                  {checked && item.warning ? <span className="block text-xs text-amber-700">{item.warning}</span> : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
